import { useTranslation } from "react-i18next";
import { useMemo, useState, useEffect } from "react";
import {
  X,
  Sparkles,
  User,
  FileQuestion,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { Button } from "../../ui/button";
import { Badge } from "../../ui/badge";
import RenderReviewDialog from "../../RenderReviewDialog";

interface PaneRecordings {
  audio?: any;
  video?: any;
  image?: any;
}

interface ExampleFollowerPaneProps {
  diagram: any;
  currentActivity: string;
  exampleLogEntries: Array<{
    activity: string;
    activityDialog: any;
    userData: Record<string, any>;
    selectedOptionId?: string;
    prescriptionLanguage?: string;
    fiPrescriptionHistory?: string;
    parentProcessId?: string;
    isCurrentScreen?: boolean;
  }>;
  exampleNotes: Record<string, any>;
  exampleName: string;
  creatorName: string;
  onClose: () => void;
  processId?: string;
  prescriptionLanguage?: string;
  prologueRecordings?: PaneRecordings;
  epilogueRecordings?: PaneRecordings;
}

const ExampleFollowerPane = ({
  diagram,
  currentActivity,
  exampleLogEntries,
  exampleNotes,
  exampleName,
  creatorName,
  onClose,
  processId,
  prescriptionLanguage = "en",
  prologueRecordings,
  epilogueRecordings,
}: ExampleFollowerPaneProps) => {
  const { t } = useTranslation();

  // State for managing multiple visits
  const [selectedVisitIndex, setSelectedVisitIndex] = useState(0);

  // Filter visits to current activity from log entries
  const visitsToCurrentActivity = useMemo(() => {
    return exampleLogEntries.filter(
      (entry) => entry.activity === currentActivity
    );
  }, [exampleLogEntries, currentActivity]);

  // Reset selected visit index when activity changes
  useEffect(() => {
    setSelectedVisitIndex(0);
  }, [currentActivity]);

  // Determine what to display
  const displayState = useMemo(() => {
    if (visitsToCurrentActivity.length === 0) {
      return { type: "never_visited" as const };
    } else if (visitsToCurrentActivity.length === 1) {
      return {
        type: "single_visit" as const,
        logEntry: visitsToCurrentActivity[0],
      };
    } else {
      return {
        type: "multiple_visits" as const,
        visits: visitsToCurrentActivity,
        currentLogEntry: visitsToCurrentActivity[selectedVisitIndex],
        totalVisits: visitsToCurrentActivity.length,
      };
    }
  }, [visitsToCurrentActivity, selectedVisitIndex]);

  // Get the logEntry to display (already in correct format from process_log)
  const logEntry = useMemo(() => {
    if (displayState.type === "never_visited") {
      return null;
    }

    // logEntries from process_log already have the correct structure
    // { activity, activityDialog, userData, prescriptionLanguage, ... }
    return displayState.type === "single_visit"
      ? displayState.logEntry
      : displayState.currentLogEntry;
  }, [displayState]);

  return (
    <div className="flex flex-col h-full border-l-4 border-purple-400 bg-gradient-to-br from-purple-50/50 to-primary/5/50">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-purple-200 shadow-sm bg-white/90 backdrop-blur-sm">
        <div className="px-4 py-3">
          <div className="flex items-start justify-between gap-3">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="flex-shrink-0 w-4 h-4 text-purple-600" />
                <Badge
                  variant="secondary"
                  className="text-purple-700 bg-purple-100 hover:bg-purple-100"
                >
                  {t("following_example") || "Following Example"}
                </Badge>
              </div>
              <h3 className="mb-1 text-sm font-semibold text-gray-900 truncate">
                {exampleName}
              </h3>
              <div className="flex items-center gap-1.5 text-xs text-gray-600">
                <User className="w-3 h-3" />
                <span>
                  {t("by") || "by"}{" "}
                  <span className="font-medium">{creatorName}</span>
                </span>
              </div>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={onClose}
              className="flex-shrink-0 w-8 h-8 p-0 text-gray-500 hover:text-gray-700 hover:bg-gray-100"
              title={t("stop_following_example") || "Stop following example"}
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Visit Selector - Only show for multiple visits */}
      {displayState.type === "multiple_visits" && (
        <div className="flex-shrink-0 px-4 py-3 bg-white border-b border-purple-200">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700">
                {t("example_visit") || "Visit"}:
              </span>
              <Badge variant="outline" className="text-xs">
                {selectedVisitIndex + 1} {t("of") || "of"}{" "}
                {displayState.totalVisits}
              </Badge>
            </div>
            <div className="flex items-center gap-1">
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setSelectedVisitIndex(Math.max(0, selectedVisitIndex - 1))
                }
                disabled={selectedVisitIndex === 0}
                className="px-2 h-7"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>
              {displayState.visits.map((_, index) => (
                <Button
                  key={index}
                  size="sm"
                  variant={selectedVisitIndex === index ? "default" : "outline"}
                  onClick={() => setSelectedVisitIndex(index)}
                  className="p-0 text-xs h-7 w-7"
                >
                  {index + 1}
                </Button>
              ))}
              <Button
                variant="outline"
                size="sm"
                onClick={() =>
                  setSelectedVisitIndex(
                    Math.min(
                      displayState.totalVisits - 1,
                      selectedVisitIndex + 1
                    )
                  )
                }
                disabled={selectedVisitIndex === displayState.totalVisits - 1}
                className="px-2 h-7"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>
          </div>
          <p className="mt-2 text-xs text-gray-600">
            {t("multiple_visits_info") ||
              "This pane was visited multiple times in the example. Use the buttons above to navigate between visits."}
          </p>
        </div>
      )}

      {/* Example Content */}
      <div className="flex-1 overflow-auto">
        {displayState.type === "never_visited" ? (
          // Never Visited Message
          <div className="flex items-center justify-center h-full p-6">
            <div className="max-w-md text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 mb-4 bg-purple-100 rounded-full">
                <FileQuestion className="w-8 h-8 text-purple-600" />
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900">
                {t("activity_not_visited_in_example") ||
                  "This activity was not visited in the example"}
              </h3>
              <p className="mb-4 text-sm text-gray-600">
                {t("activity_not_visited_description") ||
                  "The example followed a different path and does not include content for this pane."}
              </p>
            </div>
          </div>
        ) : (
          // Show example data (single or selected visit)
          logEntry && (
            <RenderReviewDialog
              logEntry={logEntry}
              onQuotePane={undefined}
              onCopyToAction={undefined}
              onCopyToInsight={undefined}
              notes={exampleNotes}
              processId={processId || "example-view"}
              isProcessOwner={false}
              isEditMode={false}
              onUserDataChange={undefined}
              onPrologueChange={undefined}
              onEpilogueChange={undefined}
              prologueRecordings={prologueRecordings}
              epilogueRecordings={epilogueRecordings}
              onNotesChange={undefined}
              exampleCreatorName={creatorName}
            />
          )
        )}
      </div>

      {/* Info Footer - Only show when displaying example data */}
      {displayState.type !== "never_visited" && (
        <div className="flex-shrink-0 px-4 py-2 border-t border-purple-200 bg-purple-50">
          <p className="text-xs text-center text-purple-700">
            {t("example_view_info") ||
              "This is a read-only view of how someone else completed this activity"}
          </p>
        </div>
      )}
    </div>
  );
};

export default ExampleFollowerPane;
